import { useState } from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  CalendarMonth,
  School,
  DirectionsCar,
  Quiz,
  EventNote,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Vocabulary } from "../../Utils/Vocabulary";
import { ExamsSectionProps, AddExamFormData } from "../../Utils/Types";
import { postData } from "../../Services/postData";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import styles from "../../Styles/clientAccount.module.css";
import moment from "moment";
import { ExamTypes } from "../../Utils/Constants";
import AddExamModal from "./AddExamModal";
import ExamCountdownTimer from "./ExamCountdownTimer";

export default function ExamsSection({
  exams,
  loading,
  hasMore,
  onLoadMore,
  onExamsRefresh,
  activeOrders,
}: ExamsSectionProps) {
  const navigate = useNavigate();
  const [openAddExam, setOpenAddExam] = useState(false);
  const [formData, setFormData] = useState<AddExamFormData>({
    type: "",
    examDate: moment().format("YYYY-MM-DD"),
    orderId: activeOrders?.length == 1 ? activeOrders[0]._id : "",
  });
  const clientId = localStorage.getItem("clientId");

  // Check if all orders are certificate orders
  const allOrdersAreCertificate = activeOrders?.every(
    (order) => order.product?.isCertificate === true
  );

  const handleAddExamClick = () => {
    if (allOrdersAreCertificate) {
      // Redirect to certificate exams calendar
      navigate(localUrlEnum.certificateExams);
    } else {
      // Open the regular exam modal
      setOpenAddExam(true);
    }
  };

  const getExamTypeIcon = (type: string) => {
    return type === ExamTypes.THEORETIC ? <Quiz /> : <DirectionsCar />;
  };

/**
 * Returns the color of the exam based on the type
 * @param {string} type The type of exam
 * @returns {string} The color of the exam
 */
  const getExamTypeColor = (type: string) => {
    return type === ExamTypes.THEORETIC ? "primary" : "secondary";
  };

  const handleAddExam = async () => {
    if (!formData.type || !formData.examDate || !formData.orderId) return;

    try {
      await postData(`${endpoints.exams}`, {
        type: formData.type,
        examDate: formData.examDate,
        orderId: formData.orderId,
        clientId,
      });

      setFormData({ type: "", examDate: moment().format("YYYY-MM-DD"), orderId: activeOrders?.length === 1 ? activeOrders[0]._id : "" });
      setOpenAddExam(false);
      onExamsRefresh();
    } catch (err) {
      console.error("Error adding exam:", err);
    }
  };

  const handleFormChange = (field: keyof AddExamFormData, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const getLastExamOfEachType = () => {
    const examsByType: { [key: string]: typeof exams[0] } = {};

    exams.forEach((exam) => {
      if (!examsByType[exam.type] || new Date(exam.examDate) > new Date(examsByType[exam.type].examDate)) {
        examsByType[exam.type] = exam;
      }
    });

    return Object.values(examsByType);
  };

  const displayedExams = getLastExamOfEachType();


  return (
    <>
      <Box className={styles.examsSectionHeader}>
        <Typography variant="h5" className={styles.examsSectionTitle}>
          {Vocabulary.scheduledExams}
        </Typography>
        <Button
          variant="contained"
          startIcon={<EventNote />}
          onClick={handleAddExamClick}
          className={styles.addExamButton}
        >
          {Vocabulary.addNewExam}
        </Button>
      </Box>

      <Grid container spacing={3} mb={"24px"}>
        {displayedExams.map((exam) => (
          <Grid item xs={12} md={6} lg={4} key={exam._id}>
            <Card className={styles.examCard} elevation={2}>
              <CardContent>
                <Box className={styles.cardHeader}>
                  <Box className={styles.examTypeContainer}>
                    {getExamTypeIcon(exam.type)}
                    <Chip
                      label={exam.type}
                      color={getExamTypeColor(exam.type)}
                      size="small"
                      className={styles.typeChip}
                    />
                  </Box>
                </Box>

                <Box className={styles.examDetails}>
                  <Box className={styles.dateContainer}>
                    <CalendarMonth className={styles.dateIcon} />
                    <Typography variant="h6" className={styles.date}>
                      {moment(exam.examDate).format("DD MMMM YYYY")}
                    </Typography>
                  </Box>
                  <ExamCountdownTimer examDate={exam.examDate} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {displayedExams.length === 0 && !loading && (
        <Box className={styles.noExams}>
          <School className={styles.noExamsIcon} />
          <Typography variant="h6">{Vocabulary.noExamsScheduled}</Typography>
        </Box>
      )}

      {hasMore && (
        <Box className={styles.loadMoreContainer}>
          <Button
            onClick={onLoadMore}
            disabled={loading}
            variant="outlined"
            className={styles.loadMoreButton}
          >
            {loading ? <CircularProgress size={20} /> : "Încarcă mai multe"}
          </Button>
        </Box>
      )}

      <AddExamModal
        open={openAddExam}
        formData={formData}
        onClose={() => {
          setOpenAddExam(false);
          setFormData({
            type: "",
            examDate: moment().format("YYYY-MM-DD"),
            orderId: activeOrders?.length === 1 ? activeOrders[0]._id : "",
          });
        }}
        onChange={handleFormChange}
        onSubmit={handleAddExam}
        activeOrders={activeOrders}
      />
    </>
  );
}
